import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import classNames from "classnames"

import BasicButton from "@/components/button/BasicButton"
import TextField from "@/components/form/textField/TextField"
import Dialog from "@/components/dialog/Dialog"
import Icon from "@/components/icon/Icon"
import { colors } from "@/components/colors"
import Radio from "@/components/form/radio/Radio"
import { useMetricList } from "@/hooks/api/useMetricQuery"
import { MetricMeta } from "@/types/metric"
import { useTranslateMetricKey, useTranslateMetric } from "@/hooks/metric/useTranslateKeyHook"

import "./metricSetting.scss"

export default function MetricSetting({
  selectedMetric,
  setSelectedMetric,
  className,
  label,
}: {
  selectedMetric: string
  setSelectedMetric: (value: string) => void
  className?: string
  label?: string
}) {
  const { t } = useTranslation()
  const translateKey = useTranslateMetricKey()
  const translateMetric = useTranslateMetric()

  const [open, setOpen] = useState(false)
  const [tempMetric, setTempMetric] = useState<string>("")
  const [metricList, setMetricList] = useState<MetricMeta[]>([])

  const { data } = useMetricList()

  useEffect(() => {
    setMetricList(data || [])
  }, [data])

  useEffect(() => {
    setTempMetric(selectedMetric)
  }, [selectedMetric, open])

  const onSearchMetric = (searchValue: string) => {
    if (searchValue === "" || searchValue.length < 2) {
      setMetricList(data || [])

      return
    }

    const filteredItems = data?.filter((item) => {
      const translatedText = translateMetric(item)?.toLowerCase() || ""
      const searchLower = searchValue.toLowerCase()

      return translatedText.includes(searchLower) || item.key.toLowerCase().includes(searchLower)
    })

    setMetricList(filteredItems || [])
  }

  return (
    <>
      <div className={classNames("metric-setting-wrapper", className)}>
        <TextField
          value={selectedMetric ? translateKey(selectedMetric) : ""}
          size="small"
          fullWidth
          label={label ?? t("metrics")}
          error={selectedMetric === ""}
          showMessage={selectedMetric === ""}
          message={t("rules.required")}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
        />
        <div className="metric-setting-edit-button">
          <BasicButton type="default-outlined" size="md" onClick={() => setOpen(true)}>
            <p
              style={{
                whiteSpace: "nowrap",
              }}>
              {t("button.edit")}
            </p>
          </BasicButton>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("editMetric")}
        confirmTextKey="button.save"
        maxWidth="600px"
        footer={
          <>
            <BasicButton type="default-outlined" size="md" onClick={() => setOpen(false)}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton
              type="primary-contained"
              size="md"
              onClick={() => {
                setSelectedMetric(tempMetric)
                setOpen(false)
              }}
              disabled={!tempMetric || selectedMetric === tempMetric}>
              {t("button.save")}
            </BasicButton>
          </>
        }>
        <div className="MetricSettingDialogCont">
          <div className="edit-metric-search-wrapper">
            <p>
              {t("metrics")}({metricList.length})
            </p>
            <TextField
              type="search"
              size="small"
              placeholder={t("search")}
              appendIcon={<Icon type="search" size={24} color={colors.neutral.grey[400]} />}
              onChange={(e) => onSearchMetric(e.target.value)}
            />
          </div>
          <div className="edit-metric-list-wrapper">
            {metricList.length ? (
              metricList.map((metric) => (
                <div key={metric.key} className="edit-metric-list-item">
                  <Radio
                    checked={tempMetric === metric.key}
                    onChange={() => setTempMetric(metric.key)}
                    label={translateMetric(metric)}
                  />
                </div>
              ))
            ) : (
              <div className="edit-metric-no-data">
                <p>{t("messages.noDataAvailable")}</p>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  )
}
